import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, Variants } from "framer-motion";
import api from "../../services/api";
import {
  Users,
  Trophy,
  BookOpen,
  MessageCircle,
  Star,
  ChevronUp,
  Zap,
  Target,
  Heart,
  Globe,
  Shield,
  TrendingUp,
} from "lucide-react";
import { sportGradient, capitalize, countdownLabel } from "../../utils/tournamentUi";

/* ============================
   Types
============================ */
interface Tournament {
  _id: string;
  title: string;
  location: string;
  date: string;
  sport?: string;
  bannerImage?: string;
  availableSpots?: number;
}

/* ============================
   Particle Background
============================ */
const ParticleBackground: React.FC<{ count?: number }> = ({ count = 32 }) => {
  const particles = useMemo(
    () =>
      Array.from({ length: count }).map((_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 3 + 1,
        dur: Math.random() * 12 + 8,
        delay: Math.random() * 5,
      })),
    [count]
  );

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {particles.map((p) => (
        <motion.span
          key={p.id}
          className="absolute rounded-full"
          style={{
            width: p.size,
            height: p.size,
            top: `${p.y}%`,
            left: `${p.x}%`,
            background:
              "linear-gradient(135deg, rgba(99,102,241,0.3), rgba(56,189,248,0.2))",
            filter: "blur(0.5px)",
          }}
          animate={{
            y: [0, -20, 0],
            x: [0, 10, 0],
            opacity: [0.2, 0.7, 0.2],
          }}
          transition={{
            duration: p.dur,
            repeat: Infinity,
            ease: "easeInOut",
            delay: p.delay,
          }}
        />
      ))}
    </div>
  );
};


/* ============================
   Motion Variants
============================ */
const fadeUp: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut", delay: i * 0.1 },
  }),
};

const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.2 },
  },
};

/* ============================
   Feature Card Component
============================ */
const FeatureCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  gradient: string;
}> = ({ icon, title, description, gradient }) => (
  <motion.div
    variants={fadeUp}
    className="group relative bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-xl hover:border-transparent transition-all duration-300"
  >
    <div
      className={`w-12 h-12 rounded-xl ${gradient} flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform`}
    >
      {icon}
    </div>
    <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
  </motion.div>
);

/* ============================
   Stat Component
============================ */
const StatItem: React.FC<{ value: string; label: string }> = ({
  value,
  label,
}) => (
  <motion.div variants={fadeUp} className="text-center">
    <div className="text-3xl sm:text-4xl font-extrabold bg-gradient-to-r from-indigo-400 to-sky-400 bg-clip-text text-transparent">
      {value}
    </div>
    <div className="text-gray-400 text-sm mt-1">{label}</div>
  </motion.div>
);

/* ============================
   Testimonial Component
============================ */
const TestimonialCard: React.FC<{
  quote: string;
  name: string;
  role: string;
  rating: number;
}> = ({ quote, name, role, rating }) => (
  <motion.div
    variants={fadeUp}
    className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
  >
    <div className="flex gap-1 mb-3">
      {Array.from({ length: rating }).map((_, i) => (
        <Star key={i} size={16} className="fill-amber-400 text-amber-400" />
      ))}
    </div>
    <p className="text-gray-700 text-sm leading-relaxed italic mb-4">
      "{quote}"
    </p>
    <div>
      <div className="font-semibold text-gray-900 text-sm">{name}</div>
      <div className="text-gray-500 text-xs">{role}</div>
    </div>
  </motion.div>
);


/* ============================
   Landing Page
============================ */
const LandingPage: React.FC = () => {
  const [featuredTournaments, setFeaturedTournaments] = useState<Tournament[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const resp = await api.get("/tournaments/featured");
        const data = Array.isArray(resp.data?.data) ? resp.data.data : [];
        if (!cancelled) setFeaturedTournaments(data);
      } catch {
        if (!cancelled) setFeaturedTournaments([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 30);
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const formatDate = (d?: string) =>
    d
      ? new Date(d).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "Date TBA";

  return (
    <div className="min-h-screen bg-white text-gray-900 overflow-x-hidden">
      {/* ===== NAVIGATION ===== */}
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "backdrop-blur-xl bg-slate-900/80 shadow-lg"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <nav className="flex items-center justify-between py-3 sm:py-4">
            <Link to="/" className="flex items-center hover:opacity-90 transition-opacity" aria-label="Sportofolo home">
              <img
                src="/logo-dark.png"
                alt="Sportofolo"
                className="h-10 sm:h-12 w-auto"
                draggable={false}
              />
            </Link>

            <div className="flex items-center gap-2 sm:gap-3">
              <Link
                to="/login"
                className="text-white/90 hover:text-white border border-white/30 hover:border-white/60 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm transition-all"
              >
                Log In
              </Link>
              <Link
                to="/signup"
                className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold bg-gradient-to-r from-indigo-500 to-sky-500 text-white shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40 transition-all"
              >
                Get Started
              </Link>
            </div>
          </nav>
        </div>
      </header>


      {/* ===== HERO SECTION ===== */}
      <section className="relative min-h-[90vh] sm:min-h-screen flex items-center bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 text-white overflow-hidden">
        <ParticleBackground count={18} />

        {/* Decorative gradient orbs */}
        <div className="absolute top-20 right-10 w-48 sm:w-72 h-48 sm:h-72 bg-indigo-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-64 sm:w-96 h-64 sm:h-96 bg-sky-500/10 rounded-full blur-3xl" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-20 pt-24 sm:py-32 w-full">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <motion.div
                variants={fadeUp}
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-4 py-1.5 mb-6"
              >
                <Zap size={14} className="text-amber-400" />
                <span className="text-sm text-gray-300">
                  India's Growing Sports Community
                </span>
              </motion.div>

              <motion.h1
                variants={fadeUp}
                className="text-3xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.1] tracking-tight"
              >
                Where Athletes{" "}
                <span className="bg-gradient-to-r from-indigo-400 via-sky-400 to-emerald-400 bg-clip-text text-transparent">
                  Unite & Grow
                </span>
              </motion.h1>

              <motion.p
                variants={fadeUp}
                className="mt-5 text-base sm:text-lg text-gray-300 max-w-lg leading-relaxed"
              >
                A vibrant platform for sports enthusiasts — Chess, Cricket, and
                Tennis. Connect with coaches, compete in tournaments, share
                knowledge, and be part of a community that celebrates every
                sport.
              </motion.p>

              <motion.div
                variants={fadeUp}
                className="mt-6 sm:mt-8 flex flex-col xs:flex-row flex-wrap gap-3"
              >
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 rounded-full bg-gradient-to-r from-indigo-500 to-sky-500 text-white font-semibold shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 transition-all hover:scale-105 text-sm sm:text-base"
                >
                  Join the Community
                  <ChevronUp size={16} className="rotate-90" />
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 rounded-full border border-white/20 text-white hover:bg-white/10 transition-all text-sm sm:text-base"
                >
                  Explore as Guest
                </Link>
              </motion.div>

              {/* Stats row */}
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
                className="mt-10 sm:mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-5 max-w-md"
              >
                <StatItem value="500+" label="Athletes" />
                <StatItem value="50+" label="Coaches" />
                <StatItem value="100+" label="Events" />
                <StatItem value="3" label="Sports" />
              </motion.div>
            </motion.div>

            {/* Hero visual - abstract */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, rotate: -2 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
              className="hidden lg:flex justify-center"
            >
              <div className="relative w-full max-w-lg">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/20 to-sky-500/20 rounded-full blur-3xl scale-90" />
                <img
                  src="/images/hero-abstract.svg"
                  alt="Abstract sports community illustration"
                  className="relative w-full h-auto drop-shadow-2xl"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>


      {/* ===== ABOUT / MISSION SECTION ===== */}
      <section className="py-14 sm:py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <motion.div
              variants={fadeUp}
              className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 rounded-full px-4 py-1.5 text-sm font-medium mb-4"
            >
              <Heart size={14} />
              Our Mission
            </motion.div>
            <motion.h2
              variants={fadeUp}
              className="text-3xl sm:text-4xl font-extrabold text-gray-900"
            >
              Building the Future of Sports in India
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="mt-4 text-gray-600 text-base sm:text-lg leading-relaxed"
            >
              We believe sports are more than games — they build discipline,
              forge friendships, and create opportunities. Sportofolo brings
              together players of all levels across Chess, Cricket, and Tennis,
              coaches who are passionate about teaching, and organizers who make
              competitive sports accessible to everyone.
            </motion.p>
          </motion.div>

          {/* Values grid */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            <FeatureCard
              icon={<Globe size={22} />}
              title="Multi-Sport Community"
              description="Chess, Cricket, and Tennis — all under one roof. Find your sport, find your people, and grow together."
              gradient="bg-gradient-to-br from-indigo-500 to-indigo-600"
            />
            <FeatureCard
              icon={<BookOpen size={22} />}
              title="Learn & Improve"
              description="Access articles, coaching sessions, and training materials shared by experienced athletes and certified coaches."
              gradient="bg-gradient-to-br from-sky-500 to-sky-600"
            />
            <FeatureCard
              icon={<Trophy size={22} />}
              title="Compete & Excel"
              description="Join regular tournaments across all sports — from friendly matches to competitive championships. Track your progress."
              gradient="bg-gradient-to-br from-amber-500 to-orange-500"
            />
            <FeatureCard
              icon={<MessageCircle size={22} />}
              title="Connect & Discuss"
              description="Chat with fellow athletes, join sport-specific communities, share highlights, and discuss strategies in real-time."
              gradient="bg-gradient-to-br from-emerald-500 to-emerald-600"
            />
            <FeatureCard
              icon={<Target size={22} />}
              title="Set Goals & Achieve"
              description="Earn achievements, track your progress across sports, and celebrate milestones with the community on our Wall of Fame."
              gradient="bg-gradient-to-br from-purple-500 to-purple-600"
            />
            <FeatureCard
              icon={<Shield size={22} />}
              title="Safe & Moderated"
              description="A respectful environment with active moderation, ensuring positive interactions and fair play for everyone."
              gradient="bg-gradient-to-br from-rose-500 to-rose-600"
            />
          </motion.div>
        </div>
      </section>


      {/* ===== WHO IS THIS FOR SECTION ===== */}
      <section className="py-14 sm:py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto mb-14"
          >
            <motion.div
              variants={fadeUp}
              className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 rounded-full px-4 py-1.5 text-sm font-medium mb-4"
            >
              <Users size={14} />
              Who It's For
            </motion.div>
            <motion.h2
              variants={fadeUp}
              className="text-3xl sm:text-4xl font-extrabold text-gray-900"
            >
              A Place for Every Sports Enthusiast
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
            className="grid sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-8"
          >
            {/* Players */}
            <motion.div
              variants={fadeUp}
              className="relative bg-white rounded-2xl p-5 sm:p-8 shadow-sm border border-gray-100 overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-24 sm:w-32 h-24 sm:h-32 bg-gradient-to-bl from-indigo-50 to-transparent rounded-bl-full" />
              <div className="relative">
                <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center mb-5">
                  <Users size={26} className="text-indigo-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Players & Athletes
                </h3>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-indigo-500 mt-0.5">•</span>
                    Find opponents and teammates in your sport
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-indigo-500 mt-0.5">•</span>
                    Join tournaments across Chess, Cricket & Tennis
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-indigo-500 mt-0.5">•</span>
                    Track your progress and rankings
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-indigo-500 mt-0.5">•</span>
                    Earn achievements and recognition
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Coaches */}
            <motion.div
              variants={fadeUp}
              className="relative bg-white rounded-2xl p-5 sm:p-8 shadow-sm border border-gray-100 overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-24 sm:w-32 h-24 sm:h-32 bg-gradient-to-bl from-emerald-50 to-transparent rounded-bl-full" />
              <div className="relative">
                <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mb-5">
                  <TrendingUp size={26} className="text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Coaches & Trainers
                </h3>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-500 mt-0.5">•</span>
                    Build your coaching profile across sports
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-500 mt-0.5">•</span>
                    Publish articles and training material
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-500 mt-0.5">•</span>
                    Connect with athletes seeking guidance
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-500 mt-0.5">•</span>
                    Organize and manage tournaments
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Community */}
            <motion.div
              variants={fadeUp}
              className="relative bg-white rounded-2xl p-5 sm:p-8 shadow-sm border border-gray-100 overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-24 sm:w-32 h-24 sm:h-32 bg-gradient-to-bl from-amber-50 to-transparent rounded-bl-full" />
              <div className="relative">
                <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mb-5">
                  <Heart size={26} className="text-amber-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Sports Enthusiasts
                </h3>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">•</span>
                    Follow top athletes and coaches
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">•</span>
                    Read articles and stay updated
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">•</span>
                    Join sport-specific community discussions
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">•</span>
                    Discover local sports events near you
                  </li>
                </ul>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>


      {/* ===== TOURNAMENTS SECTION ===== */}
      <section className="py-14 sm:py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-10"
          >
            <div>
              <motion.div
                variants={fadeUp}
                className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 rounded-full px-4 py-1.5 text-sm font-medium mb-4"
              >
                <Trophy size={14} />
                Compete
              </motion.div>
              <motion.h2
                variants={fadeUp}
                className="text-3xl sm:text-4xl font-extrabold text-gray-900"
              >
                Upcoming Tournaments
              </motion.h2>
              <motion.p
                variants={fadeUp}
                className="mt-2 text-gray-600 max-w-lg"
              >
                Regular events across Chess, Cricket, and Tennis. Sign up and
                test your skills against the best.
              </motion.p>
            </div>
            <motion.div variants={fadeUp}>
              <Link
                to="/tournaments"
                className="inline-flex items-center gap-1 text-indigo-600 font-medium text-sm hover:text-indigo-700 mt-4 sm:mt-0"
              >
                View all tournaments
                <ChevronUp size={14} className="rotate-90" />
              </Link>
            </motion.div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {loading
              ? Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className="bg-gray-100 h-72 rounded-2xl animate-pulse"
                  />
                ))
              : featuredTournaments.map((t) => {
                  const countdown = countdownLabel(t.date);
                  const spots = t.availableSpots;
                  return (
                    <motion.article
                      key={t._id}
                      variants={fadeUp}
                      className="group bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow"
                    >
                      <Link
                        to={`/tournaments/${t._id}`}
                        className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded-2xl"
                      >
                        <div
                          className={`relative h-48 overflow-hidden bg-gradient-to-br ${sportGradient(
                            t.sport
                          )}`}
                        >
                          {t.bannerImage ? (
                            <div
                              className="absolute inset-0 bg-cover bg-center group-hover:scale-105 transition-transform duration-500"
                              style={{ backgroundImage: `url(${t.bannerImage})` }}
                            />
                          ) : (
                            <Trophy
                              className="absolute right-5 top-5 text-white/20"
                              size={72}
                            />
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                          {t.sport && (
                            <span className="absolute top-3 left-4 text-xs font-semibold uppercase tracking-widest text-white/90">
                              {capitalize(t.sport)}
                            </span>
                          )}
                          <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between">
                            <span className="bg-white/90 backdrop-blur-sm text-gray-900 text-xs font-medium px-2.5 py-1 rounded-full">
                              {formatDate(t.date)}
                            </span>
                            {countdown && (
                              <span className="bg-indigo-600 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                                {countdown}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="p-5">
                          <h3 className="font-bold text-lg text-gray-900 group-hover:text-indigo-700 transition-colors">
                            {t.title}
                          </h3>
                          <div className="mt-1 flex items-center justify-between text-sm text-gray-500">
                            <p className="flex items-center gap-1">
                              <Globe size={13} />
                              {t.location}
                            </p>
                            {typeof spots === "number" && (
                              <span
                                className={
                                  spots <= 0
                                    ? "text-red-600 font-medium"
                                    : spots <= 5
                                    ? "text-amber-600 font-medium"
                                    : ""
                                }
                              >
                                {spots <= 0 ? "Full" : `${spots} spots left`}
                              </span>
                            )}
                          </div>
                        </div>
                      </Link>
                    </motion.article>
                  );
                })}
          </motion.div>

          {!loading && featuredTournaments.length === 0 && (
            <div className="text-center py-12 rounded-2xl border border-dashed border-gray-300">
              <Trophy className="mx-auto text-gray-300 mb-3" size={40} />
              <p className="font-semibold text-gray-900">No upcoming tournaments yet</p>
              <p className="text-gray-500 text-sm mt-1">
                New events will appear here as soon as they are announced.
              </p>
            </div>
          )}
        </div>
      </section>


      {/* ===== TESTIMONIALS SECTION ===== */}
      <section className="py-14 sm:py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto mb-14"
          >
            <motion.div
              variants={fadeUp}
              className="inline-flex items-center gap-2 bg-purple-50 text-purple-700 rounded-full px-4 py-1.5 text-sm font-medium mb-4"
            >
              <Star size={14} />
              Community Voices
            </motion.div>
            <motion.h2
              variants={fadeUp}
              className="text-3xl sm:text-4xl font-extrabold text-gray-900"
            >
              What Our Members Say
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-6"
          >
            <TestimonialCard
              quote="Sportofolo helped me find a chess coach who understood my playing style. My rating jumped 200 points in just 3 months!"
              name="Arjun Mehta"
              role="Chess Player, Pune"
              rating={5}
            />
            <TestimonialCard
              quote="As a cricket coach, I love how easy it is to publish training articles and connect with players who are genuinely passionate about improving."
              name="Priya Sharma"
              role="Cricket Coach, Mumbai"
              rating={5}
            />
            <TestimonialCard
              quote="The tournament system is seamless across all sports. I've participated in tennis and chess events — the community is incredibly supportive."
              name="Rahul Krishnan"
              role="Tennis Player, Bangalore"
              rating={5}
            />
          </motion.div>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="relative py-14 sm:py-20 lg:py-28 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-10 left-1/4 w-64 h-64 bg-indigo-500/30 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-1/4 w-80 h-80 bg-sky-500/20 rounded-full blur-3xl" />
        </div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="relative z-10 max-w-3xl mx-auto px-4 sm:px-5 text-center"
        >
          <motion.h2
            variants={fadeUp}
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight"
          >
            Ready to Make Your{" "}
            <span className="bg-gradient-to-r from-indigo-400 to-sky-400 bg-clip-text text-transparent">
              Next Move
            </span>
            ?
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="mt-4 text-gray-300 text-base sm:text-lg max-w-xl mx-auto"
          >
            Join hundreds of athletes and coaches across Chess, Cricket, and
            Tennis who are already part of this growing community. It's free to
            get started.
          </motion.p>
          <motion.div
            variants={fadeUp}
            className="mt-6 sm:mt-8 flex flex-col xs:flex-row flex-wrap justify-center gap-3"
          >
            <Link
              to="/signup"
              className="inline-flex items-center justify-center gap-2 px-5 sm:px-7 py-3 sm:py-3.5 rounded-full bg-white text-slate-900 font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all text-sm sm:text-base"
            >
              Create Free Account
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 px-5 sm:px-7 py-3 sm:py-3.5 rounded-full border border-white/20 text-white hover:bg-white/10 transition-all text-sm sm:text-base"
            >
              Sign In
            </Link>
          </motion.div>
        </motion.div>
      </section>


      {/* ===== FOOTER ===== */}
      <footer className="bg-slate-900 text-white border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {/* Brand */}
            <div className="col-span-2 lg:col-span-1">
              <img
                src="/logo-dark.png"
                alt="Sportofolo"
                className="h-12 sm:h-14 w-auto mb-4"
              />
              <p className="text-gray-400 text-sm leading-relaxed max-w-xs">
                Building India's most welcoming sports community — one game at a
                time. Chess, Cricket, and Tennis.
              </p>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-semibold text-sm mb-3 text-gray-300">
                Platform
              </h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <Link to="/signup" className="hover:text-white transition">
                    Join Community
                  </Link>
                </li>
                <li>
                  <Link to="/login" className="hover:text-white transition">
                    Sign In
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-sm mb-3 text-gray-300">
                Community
              </h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <span className="hover:text-white transition cursor-pointer">
                    Tournaments
                  </span>
                </li>
                <li>
                  <span className="hover:text-white transition cursor-pointer">
                    Articles
                  </span>
                </li>
                <li>
                  <span className="hover:text-white transition cursor-pointer">
                    Wall of Fame
                  </span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-sm mb-3 text-gray-300">
                Connect
              </h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <Link to="/about" className="hover:text-white transition">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="hover:text-white transition">
                    Contact
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="hover:text-white transition">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Sportofolo. All rights reserved.
            </p>
            <p className="text-gray-500 text-xs">
              Made with ❤️ for the sports community
            </p>
          </div>
        </div>
      </footer>

      {/* ===== SCROLL TO TOP ===== */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="fixed bottom-6 right-6 bg-indigo-600 hover:bg-indigo-700 text-white p-3 rounded-full shadow-lg shadow-indigo-600/30 transition-colors z-40"
            aria-label="Scroll to top"
          >
            <ChevronUp size={20} />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LandingPage;
